'use client';

import React, { useState, useEffect } from 'react';

import { useGlobal } from '@/contexts/GlobalContext';
import { useModal } from '@/contexts/ModalContext';
import OBOGLabel from '@/components/teams/OBOGLabel';
import { TeamMemberGlobalContextModel, Status } from '@/types/teams';
import { getFilePath } from '@/utils';

interface TeamNoteSwitchModalProps {
  propsSelectedValue: string;
  onChange: (value: string) => void;
}

const TeamNoteSwitchModal: React.FC<TeamNoteSwitchModalProps> = ({ propsSelectedValue, onChange }) => {
  /**
   * react context
   */
  const context = useGlobal();
  const { closeModal } = useModal();

  /**
   * useState
   */
  const [userTeams, setUserTeams] = useState<TeamMemberGlobalContextModel[]>([]);
  const [selectedValue, setSelectedValue] = useState<string>(propsSelectedValue);

  // 「マイノートとして登録」を選択できるかどうか
  const canSelectMyNote = () => {
    // 課金機能リリース前の場合、は選択可能とする。
    // NOTE: チーム機能のテスト運用の考慮。この時点ではチケット決済を有効としないでのどちらでも登録できるように許容する。
    if (!context.isFeatureEnabled('ticket_payment')) {
      return true;
    }
    // 課金機能リリース後、チームプランのみ適用中の場合は選択不可
    if (context.user?.has_team_plan_only) {
      return false;
    }
    return true;
  };

  /**
   * モーダル閉じる
   */
  const handleModalClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // モーダルの背景をクリックした場合にだけ closeModal を呼び出す
    const modalBackground = document.getElementById('modal-background');
    if (e.target === modalBackground) {
      closeModal();
    }
  };

  const handleChange = (value: string) => {
    setSelectedValue(value);
    onChange(value);
  };

  useEffect(() => {
    // 所属状態が承認済み、OGOBのリストに限定する
    const teams = context.user_teams?.filter((ut) => ut.status === Status.Approved || ut.status === Status.OBOG);
    setUserTeams(teams || []);
  }, [context.user_teams]);

  useEffect(() => {
    setSelectedValue(propsSelectedValue);
  }, [propsSelectedValue]);

  return (
    <div
      id="modal-background"
      className="fixed inset-0 z-30 w-full flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none select-none"
      onClick={handleModalClick}>
      <div className="relative w-11/12 sm:w-6/12 max-h-[85dvh] overflow-y-auto rounded-lg">
        <div className="relative flex flex-col bg-white border-0 rounded-lg outline-none focus:outline-none">
          <div className="flex justify-between items-center border-b border-green-500 px-4 py-2 bg-white sticky top-0 z-10 rounded-t-lg">
            <h3 className="flex items-center font-footballlog italic text-sm font-semibold text-gray-500 dark:text-white">
              チームノート切り替え
            </h3>
            <button
              type="button"
              onClick={closeModal}
              className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 flex items-center justify-center dark:hover:bg-gray-600 dark:hover:text-white">
              <svg
                className="w-3 h-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14">
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
              <span className="sr-only">Close menu</span>
            </button>
          </div>
          <div className="p-4">
            <div className="">
              <ul className="grid w-full gap-6 md:grid-cols-2">
                <li key="mynote">
                  <input
                    type="radio"
                    id="mynote"
                    name="teamuuid"
                    className="hidden peer"
                    value=""
                    checked={selectedValue === ''}
                    onChange={() => handleChange('')}
                    disabled={!canSelectMyNote()}
                  />
                  <label
                    htmlFor="mynote"
                    className={`inline-flex items-center justify-between w-full p-5 border border-gray-200 rounded-lg ${canSelectMyNote() ? 'text-gray-500 bg-white cursor-pointer hover:text-gray-600 hover:bg-gray-100' : 'text-gray-400 bg-gray-50 cursor-not-allowed'} peer-checked:border-green-400 peer-checked:text-green-400 `}>
                    <div className="block">
                      <div className="flex items-center w-full">
                        <svg
                          className="w-6 h-6 me-2"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          fill="none"
                          viewBox="0 0 24 24">
                          <path
                            stroke="currentColor"
                            strokeWidth="2"
                            d="M7 17v1a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1a3 3 0 0 0-3-3h-4a3 3 0 0 0-3 3Zm8-9a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                          />
                        </svg>
                        <div className="text-lg font-semibold">マイノート</div>
                      </div>
                      {canSelectMyNote() ? (
                        <div className="w-full text-sm">マイノートとして記録</div>
                      ) : (
                        <div className="w-full text-xs">選択不可: チケット有効期限切れ</div>
                      )}
                    </div>
                    {selectedValue === '' && (
                      <svg
                        className="w-5 h-5"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 16 12">
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M1 5.917 5.724 10.5 15 1.5"
                        />
                      </svg>
                    )}
                  </label>
                </li>
                {userTeams.map((userTeam) => (
                  <React.Fragment key={userTeam.team.uuid}>
                    <li>
                      <input
                        type="radio"
                        id={`teamnote_${userTeam.team.uuid}`}
                        name="teamuuid"
                        className="hidden peer"
                        value={userTeam.team.uuid}
                        checked={selectedValue === userTeam.team.uuid}
                        onChange={() => handleChange(userTeam.team.uuid)}
                        disabled={userTeam.status === Status.OBOG}
                      />
                      <label
                        htmlFor={`teamnote_${userTeam.team.uuid}`}
                        className="relative inline-flex items-center justify-between w-full p-5 text-gray-500 bg-white border border-gray-200 rounded-lg cursor-pointer dark:hover:text-gray-300 dark:border-gray-700 dark:peer-checked:text-blue-500 peer-checked:border-green-400 peer-checked:text-green-400 hover:text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:bg-gray-800 dark:hover:bg-gray-700">
                        {userTeam.status === Status.OBOG && (
                          <div className="absolute top-1 right-2">
                            <OBOGLabel />
                          </div>
                        )}
                        <div className="block">
                          <div className="flex items-center w-full">
                            {userTeam.team.team_emblem ? (
                              <img
                                src={getFilePath(userTeam.team.team_emblem.file_path)}
                                alt="teamEmblem"
                                className="w-6 h-6 me-2 object-contain"
                              />
                            ) : (
                              <svg
                                className="w-6 h-6 me-2"
                                aria-hidden="true"
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                fill="none"
                                viewBox="0 0 24 24">
                                <path
                                  stroke="currentColor"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M12 20a16.405 16.405 0 0 1-5.092-5.804A16.694 16.694 0 0 1 5 6.666L12 4l7 2.667a16.695 16.695 0 0 1-1.908 7.529A16.406 16.406 0 0 1 12 20Z"
                                />
                              </svg>
                            )}
                            <div className="text-lg font-semibold">{userTeam.team.name}</div>
                          </div>
                          {userTeam.status === Status.OBOG ? (
                            <div className="w-full text-sm">OBOGの場合、再選択できません</div>
                          ) : (
                            <div className="w-full text-sm">のチームノートとして記録</div>
                          )}
                        </div>
                        {selectedValue === userTeam.team.uuid && (
                          <svg
                            className="w-5 h-5"
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 16 12">
                            <path
                              stroke="currentColor"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M1 5.917 5.724 10.5 15 1.5"
                            />
                          </svg>
                        )}
                      </label>
                    </li>
                  </React.Fragment>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamNoteSwitchModal;
